import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TextInput } from './common/TextInput';
import { Button } from './common/Button';
import { setAuthData } from '../utils/auth';
import { api } from '../utils/api';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const request = await api.post('/login', formData);
      const data = await request.json()
      
      // Store token in localStorage
      setAuthData(data.token);
      
      toast.success('Login successful!');
      // Redirect to home page after successful login
      navigate('/');
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle bruteforce protection errors
      if (error.response?.status === 429 && error.response?.data?.details?.type === 'bruteforce_protection') {
        const details = error.response.data.details;
        const remainingTime = details.remainingTime;
        const reason = details.reason === 'email' ? 'email address' : 'IP address';
        
        toast.error(
          `Too many failed login attempts. Your ${reason} has been temporarily blocked. Please try again in ${remainingTime} minute${remainingTime !== 1 ? 's' : ''}.`,
          { autoClose: 10000 }
        );
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.error || error.message || 'An error occurred during login';
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10 w-full space-y-8">
      <h2 className="text-center text-3xl font-extrabold text-gray-900">
        Sign in to your account
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="rounded-md shadow-sm overflow">
          <TextInput
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="Email address"
            value={formData.email}
            className="rounded-none rounded-t-md"
            size="lg"
            onChange={handleInputChange}
          />
          <TextInput
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            placeholder="Password"
            value={formData.password}
            className="rounded-none rounded-b-md border-t-0"
            size="lg"
            onChange={handleInputChange}
          />
        </div>

        <div className="mt-8">
          <Button
            disabled={isLoading}
            type="submit"
            size="lg"
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </div>
            ) : (
              'Sign in'
            )}
          </Button>
        </div>
      </form>

    </div>
  );
};

export default LoginPage; 